import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ProtocolDocSelector from "./ProtocolDocSelector";
import { parseProtocolToCriteria } from "./protocolToCriteria";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ProtocolTableEditor from "./ProtocolTableEditor";

// ─── Initial template markdown for empty protocol ───────────────────────────
const INITIAL_PROTOCOL_MARKDOWN = `## Research Questions
- **RQ1:** Add your research questions manually, or  
- **RQ2:** Generate them using AI by clicking the button above.
-

## PICO Criteria Table
| Criteria      | Include | Exclude |
|---------------|---------|---------|
| Population    |         |         |
| Intervention  |         |         |
| Comparator    |         |         |
| Outcome       |         |         |
| Study Design  |         |         |

## Hierarchy of Selection
| # | Criterion | Rule | Stage | Exclusion Reason if Not Met |
|---|-----------|------|-------|------------------------------|
| 1 |           | Must have | Both |  |
| 2 |           | Must have | Both |  |
| 3 |           | Must have | Both |  |
| 4 |           | Must have one of | Both |  |
| 5 |           | Must have | Both |  |
`;

// ─── Section header markers ─────────────────────────────────────────────────
const PICO_HEADER      = "## PICO Criteria Table";
const HIERARCHY_HEADER = "## Hierarchy of Selection";

// ─── Default table rows (used when markdown has no matching table) ───────────
const DEFAULT_PICO_ROWS = [
    ["Population",   "", ""],
    ["Intervention", "", ""],
    ["Comparator",   "", ""],
    ["Outcome",      "", ""],
    ["Study Design", "", ""],
];

const DEFAULT_HIERARCHY_ROWS = [
    ["", "Must have",        "Both", ""],
    ["", "Must have",        "Both", ""],
    ["", "Must have",        "Both", ""],
    ["", "Must have one of", "Both", ""],
    ["", "Must have",        "Both", ""],
];

// ─── Markdown table parser ───────────────────────────────────────────────────
/**
 * Parse a markdown table section (text already split from the full markdown)
 * into a 2-D array of cell strings.
 * – Skips the header row (first non-separator pipe row).
 * – Skips separator rows (|---|---|).
 * – Replaces <br> / <br /> with \n so multi-line content is editable.
 * – Pads / truncates each row to exactly numCols cells.
 */
function parseMdTableRows(tableText, numCols) {
    const lines = (tableText || "").split("\n");
    const rows = [];
    let skipHeader = true;
    for (const line of lines) {
        const t = line.trim();
        if (!t || !t.startsWith("|")) continue;
        if (/^\|[-:\s|]+\|$/.test(t)) continue;       // separator row
        if (skipHeader) { skipHeader = false; continue; } // first pipe row = header
        const cells = t
            .replace(/^\|/, "")
            .replace(/\|$/, "")
            .split("|")
            .map(c => c.trim()); // keep as-is — cells now store HTML, <br> renders in div
        while (cells.length < numCols) cells.push("");
        rows.push(cells.slice(0, numCols));
    }
    return rows;
}

// ─── Cell HTML ↔ Markdown helpers ────────────────────────────────────────────

/**
 * Convert a contentEditable cell's innerHTML to a single-line markdown string
 * safe for storage in a markdown table cell (no literal newlines).
 * Lists → bullet/numbered prefix + <br> separators.
 * Block elements → <br> separators.
 * Inline bold/italic/underline → kept as HTML tags.
 */
function cellHtmlToMd(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    // <ul> → "• item1<br>• item2"
    tmp.querySelectorAll("ul").forEach(ul => {
        const lines = [...ul.querySelectorAll("li")]
            .map(li => "• " + li.innerHTML.trim())
            .join("<br>");
        ul.outerHTML = lines;
    });

    // <ol> → "1. item1<br>2. item2"
    tmp.querySelectorAll("ol").forEach(ol => {
        const lines = [...ol.querySelectorAll("li")]
            .map((li, i) => `${i + 1}. ` + li.innerHTML.trim())
            .join("<br>");
        ol.outerHTML = lines;
    });

    // Block elements (div, p) → <br> line break before each non-first block
    tmp.querySelectorAll("div, p").forEach((el) => {
        if (el.parentElement && el !== tmp.firstElementChild) {
            el.insertAdjacentHTML("beforebegin", "<br>");
        }
        el.replaceWith(...el.childNodes);
    });

    return tmp.innerHTML
        .replace(/\n/g, "")                              // strip DOM whitespace newlines
        .replace(/^(<br\s*\/?>)+|(<br\s*\/?>)+$/gi, "") // trim leading/trailing <br>
        .trim();
}

/**
 * Convert a markdown cell string back to HTML for contentEditable display.
 * Handles backward-compat **bold** / *italic* markdown syntax as well as
 * existing <br>, <b>, <i>, <u> HTML that was stored in previous saves.
 */
function mdCellToHtml(text) {
    if (!text) return "";
    return text
        .replace(/\*\*([^*\n]+)\*\*/g, "<b>$1</b>")
        .replace(/\*([^*\n]+)\*/g, "<i>$1</i>");
    // <br>, <u>, <b>, <i> passthrough — already valid HTML
}

/**
 * Strip all HTML tags and return plain text.
 * Used in the reorder modal where labels must be plain strings.
 */
function htmlToPlainText(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent?.trim() || tmp.innerText?.trim() || "";
}

// ─── Serializers ─────────────────────────────────────────────────────────────
function serializePicoTable(rows) {
    const header = "| Criteria | Include | Exclude |";
    const sep    = "|----------|---------|---------|";
    const body   = rows.map(([c, inc, exc]) =>
        `| ${cellHtmlToMd(c)} | ${cellHtmlToMd(inc)} | ${cellHtmlToMd(exc)} |`
    );
    return [header, sep, ...body].join("\n");
}

function serializeHierarchyTable(rows) {
    const header = "| # | Criterion | Rule | Stage | Exclusion Reason if Not Met |";
    const sep    = "|---|-----------|------|-------|------------------------------|";
    const body   = rows.map(([crit, rule, stage, reason], i) =>
        `| ${i + 1} | ${cellHtmlToMd(crit)} | ${cellHtmlToMd(rule)} | ${cellHtmlToMd(stage)} | ${cellHtmlToMd(reason)} |`
    );
    return [header, sep, ...body].join("\n");
}

/**
 * Stitch the four parts back into a single protocol markdown string.
 */
function assembleMarkdown(rqMd, picoRows, hierarchyRows, afterMd) {
    const safePico = picoRows.length > 0       ? picoRows       : DEFAULT_PICO_ROWS.map(r => [...r]);
    const safeHier = hierarchyRows.length > 0  ? hierarchyRows  : DEFAULT_HIERARCHY_ROWS.map(r => [...r]);
    const parts = [
        (rqMd || "").trimEnd(),
        "\n\n" + PICO_HEADER,
        serializePicoTable(safePico),
        "\n\n" + HIERARCHY_HEADER,
        serializeHierarchyTable(safeHier),
    ];
    if (afterMd && afterMd.trim()) parts.push("\n\n" + afterMd.trim());
    return parts.join("\n");
}

/**
 * Split the full protocol markdown into its four constituent parts.
 * Falls back to default rows when the expected tables are absent.
 * Uses case-insensitive regex so slight AI header variations don't break parsing.
 */
function parseMarkdownSections(md) {
    const normalized = (md || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    // Case-insensitive, whitespace-flexible — handles "## PICO Criteria table",
    // "##PICO Criteria Table", "## pico criteria table", etc.
    const picoMatch = /##\s*PICO\s+Criteria\s+Table\b/i.exec(normalized);
    const hierMatch = /##\s*Hierarchy\s+of\s+Selection\b/i.exec(normalized);

    const picoIdx = picoMatch ? picoMatch.index : -1;
    const hierIdx = hierMatch ? hierMatch.index : -1;
    const picoLen = picoMatch ? picoMatch[0].length : PICO_HEADER.length;
    const hierLen = hierMatch ? hierMatch[0].length : HIERARCHY_HEADER.length;

    let rqMd        = normalized.trimEnd();
    let picoSection = "";
    let hierSection = "";
    let afterMd     = "";

    if (picoIdx !== -1 && hierIdx !== -1 && hierIdx > picoIdx) {
        rqMd        = normalized.slice(0, picoIdx).trimEnd();
        picoSection = normalized.slice(picoIdx + picoLen, hierIdx);
        hierSection = normalized.slice(hierIdx + hierLen);
    } else if (picoIdx !== -1) {
        rqMd        = normalized.slice(0, picoIdx).trimEnd();
        picoSection = normalized.slice(picoIdx + picoLen);
    } else if (hierIdx !== -1) {
        rqMd        = normalized.slice(0, hierIdx).trimEnd();
        hierSection = normalized.slice(hierIdx + hierLen);
    }

    // Separate afterMd: non-table content after the last pipe row of the hierarchy block
    if (hierSection) {
        const lines = hierSection.split("\n");
        let lastPipeIdx = -1;
        lines.forEach((l, i) => { if (l.trim().startsWith("|")) lastPipeIdx = i; });
        if (lastPipeIdx !== -1 && lastPipeIdx < lines.length - 1) {
            afterMd    = lines.slice(lastPipeIdx + 1).join("\n").trim();
            hierSection = lines.slice(0, lastPipeIdx + 1).join("\n");
        }
    }

    // PICO: 3 cols [criteria, include, exclude] — convert to HTML for contentEditable
    let picoRows = parseMdTableRows(picoSection, 3);
    if (picoRows.length === 0) picoRows = DEFAULT_PICO_ROWS.map(r => [...r]);
    else picoRows = picoRows.map(row => row.map(cell => mdCellToHtml(cell)));

    // Hierarchy: 5 cols [#, criterion, rule, stage, reason] → drop # (col 0)
    const rawHier = parseMdTableRows(hierSection, 5);
    const hierarchyRows = rawHier.length > 0
        ? rawHier.map(r => [r[1], r[2], r[3], r[4]].map(cell => mdCellToHtml(cell || "")))
        : DEFAULT_HIERARCHY_ROWS.map(r => [...r]);

    return { rqMd, picoRows, hierarchyRows, afterMd };
}

// ── Highlight API name constant ──────────────────────────────────────────────
const HIGHLIGHT_NAME = "slr-editor-selection";

// ─── RQ markdown ↔ HTML converters ───────────────────────────────────────────

/** Convert inline markdown (bold/italic) to HTML — used within block elements */
function mdInlineToHtml(text) {
    return (text || "")
        .replace(/\*\*([^*\n]+)\*\*/g, "<b>$1</b>")
        .replace(/\*([^*\n]+)\*/g, "<i>$1</i>")
        .replace(/__([^_\n]+)__/g, "<u>$1</u>");
}

/**
 * Convert the Research Questions markdown section to HTML for contentEditable.
 * Handles: ## headings, - / * / 1. lists, **bold**, *italic*, blank lines.
 */
function rqMdToHtml(md) {
    if (!md) return "";
    const lines = md.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    const parts = [];
    let inUl = false;
    let inOl = false;

    const closeLists = () => {
        if (inUl) { parts.push("</ul>"); inUl = false; }
        if (inOl) { parts.push("</ol>"); inOl = false; }
    };

    lines.forEach((line) => {
        const t = line.trimEnd();

        const h1 = t.match(/^#\s+(.+)$/);
        if (h1) { closeLists(); parts.push(`<p class="ev-bold-med ev-reg-p-dark-blue mb-1 mt-2">${mdInlineToHtml(h1[1])}</p>`); return; }

        const h2 = t.match(/^##\s+(.+)$/);
        if (h2) { closeLists(); parts.push(`<p class="ev-bold-med ev-reg-p-dark-blue mb-1 mt-1">${mdInlineToHtml(h2[1])}</p>`); return; }

        const h3 = t.match(/^###\s+(.+)$/);
        if (h3) { closeLists(); parts.push(`<p class="ev-bold-reg ev-small-p-blue mb-1">${mdInlineToHtml(h3[1])}</p>`); return; }

        const ulItem = t.match(/^[-*]\s?(.*)$/);
        if (ulItem) {
            if (inOl) { parts.push("</ol>"); inOl = false; }
            if (!inUl) { parts.push(`<ul class="ev-small-p-dark">`); inUl = true; }
            parts.push(`<li>${mdInlineToHtml(ulItem[1])}</li>`);
            return;
        }

        const olItem = t.match(/^\d+\.\s+(.*)$/);
        if (olItem) {
            if (inUl) { parts.push("</ul>"); inUl = false; }
            if (!inOl) { parts.push(`<ol class="ev-small-p-dark">`); inOl = true; }
            parts.push(`<li>${mdInlineToHtml(olItem[1])}</li>`);
            return;
        }

        closeLists();
        if (!t.trim()) {
            parts.push("<div><br></div>");
        } else {
            parts.push(`<div class="ev-small-p-dark">${mdInlineToHtml(t)}</div>`);
        }
    });

    closeLists();
    return parts.join("");
}

/**
 * Convert contentEditable innerHTML back to Research Questions markdown.
 * Mirrors rqMdToHtml in reverse: headings, lists, bold, italic, line breaks.
 */
function rqHtmlToMd(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    const inlineToMd = (el) => {
        let out = "";
        el.childNodes.forEach((node) => {
            if (node.nodeType === 3) { out += node.textContent; return; }
            const n = node.nodeName;
            if (n === "B" || n === "STRONG") { out += `**${inlineToMd(node)}**`; return; }
            if (n === "I" || n === "EM")     { out += `*${inlineToMd(node)}*`;  return; }
            if (n === "U")                    { out += inlineToMd(node);          return; }
            if (n === "BR")                   { out += "  \n";                    return; }
            out += inlineToMd(node);
        });
        return out;
    };

    const lines = [];
    const processNode = (node) => {
        const n = node.nodeName;
        if (n === "H1") { lines.push(`# ${inlineToMd(node).trim()}`);   return; }
        if (n === "H2") { lines.push(`## ${inlineToMd(node).trim()}`);  return; }
        if (n === "H3") { lines.push(`### ${inlineToMd(node).trim()}`); return; }
        if (n === "UL") {
            node.querySelectorAll(":scope > li").forEach(li => lines.push(`- ${inlineToMd(li).trim()}`));
            return;
        }
        if (n === "OL") {
            [...node.querySelectorAll(":scope > li")].forEach((li, i) =>
                lines.push(`${i + 1}. ${inlineToMd(li).trim()}`)
            );
            return;
        }
        if (n === "BR") { lines.push(""); return; }
        if (n === "DIV" || n === "P") {
            const cls = node.className || "";
            const text = inlineToMd(node).trim();
            // Detect headings stored as <p class="ev-bold-med ev-reg-p-dark-blue mb-1 mt-2"> etc.
            if (cls.includes("ev-bold-med") && cls.includes("ev-reg-p-dark-blue")) {
                if (cls.includes("mt-2")) { lines.push(`# ${text}`);   return; } // h1
                lines.push(`## ${text}`);  return; // h2
            }
            if (cls.includes("ev-bold-reg") && cls.includes("ev-small-p-blue")) {
                lines.push(`### ${text}`); return; // h3
            }
            // div with only <br> = blank line
            lines.push(text === "" || node.innerHTML === "<br>" ? "" : text);
            return;
        }
        if (n === "#text") {
            const t = node.textContent;
            if (t.trim()) lines.push(t.trim());
            return;
        }
        node.childNodes.forEach(child => processNode(child));
    };

    tmp.childNodes.forEach(child => processNode(child));
    return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}


// ─────────────────────────────────────────────────────────────────────────────
const ScreeningProtocolForm = ({ projectId, configId, protocolMarkdown, setProtocolMarkdown, abstractsCount = 0, onOpenGenbi }) => {
    const navigate = useNavigate();
    const API_BASE_URL = "/pceapi/slr/form";
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    const rqCellRef      = useRef(null);   // contentEditable div for Research Questions
    const isRqInputRef   = useRef(false);  // suppress DOM sync after user keystroke
    const mountedRef      = useRef(false);
    // Prevents the protocolMarkdown-change watcher from re-parsing after an internal assembly
    const isInternalUpdateRef = useRef(false);

    const [aiPrompt, setAiPrompt]   = useState("");
    const [isGenerating, setIsGenerating] = useState(false);

    const [aiMode, setAiMode] = useState(true);
    const templateFileRef = useRef(null);
    const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
    const [templateNotice, setTemplateNotice] = useState("");

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");

    // ── GenBI Report template picker ─────────────────────────────────────────
    const [showReportDropdown, setShowReportDropdown] = useState(false);
    const [genbiTemplates, setGenbiTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // ── Import protocol selector ─────────────────────────────────────────────
    const importSelectorRef = useRef(null);
    const importBtnRef      = useRef(null);
    const [isImportSelectorOpen, setIsImportSelectorOpen] = useState(false);
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
    const [reorderRows, setReorderRows] = useState(null); // null = modal closed
    const savedContentRef = useRef(null);

    const [rqMarkdown, setRqMarkdown] = useState(() => {
        const md = protocolMarkdown?.trim() ? protocolMarkdown : INITIAL_PROTOCOL_MARKDOWN;
        return parseMarkdownSections(md).rqMd;
    });
    const [picoRows, setPicoRows] = useState(() => {
        const md = protocolMarkdown?.trim() ? protocolMarkdown : INITIAL_PROTOCOL_MARKDOWN;
        return parseMarkdownSections(md).picoRows;
    });
    const [hierarchyRows, setHierarchyRows] = useState(() => {
        const md = protocolMarkdown?.trim() ? protocolMarkdown : INITIAL_PROTOCOL_MARKDOWN;
        return parseMarkdownSections(md).hierarchyRows;
    });
    const [afterMd, setAfterMd] = useState(() => {
        const md = protocolMarkdown?.trim() ? protocolMarkdown : INITIAL_PROTOCOL_MARKDOWN;
        return parseMarkdownSections(md).afterMd;
    });

    // Close import selector on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                importSelectorRef.current &&
                !importSelectorRef.current.contains(e.target) &&
                importBtnRef.current &&
                !importBtnRef.current.contains(e.target)
            ) {
                setIsImportSelectorOpen(false);
            }
        };
        if (isImportSelectorOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isImportSelectorOpen]);

    // Track mount state — assembly and external-change effects both gate on this
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // ── Selection highlight helpers ──────────────────────────────────────────
    const editorWrapperRef  = useRef(null);
    const contentAreaRef    = useRef(null); // wraps editor + tables; used for selection scope
    const [selectionPopup, setSelectionPopup] = useState({ visible: false, x: 0, y: 0, selectedText: "" });
    const [refinePrompt, setRefinePrompt]     = useState("");
    const selectionPopupRef = useRef(null);
    const refineInputRef    = useRef(null);

    // Sync rqMarkdown into the contentEditable DOM after external changes
    // (AI generation, import). Skipped when the change came from user input.
    useEffect(() => {
        if (isRqInputRef.current) { isRqInputRef.current = false; return; }
        const el = rqCellRef.current;
        if (!el) { console.warn('[Protocol] rqMarkdown sync: el is null'); return; }
        const newHtml = rqMdToHtml(rqMarkdown || "");
        console.log('[Protocol] rqMarkdown sync: setting innerHTML, newHtml length:', newHtml.length);
        if (el.innerHTML !== newHtml) el.innerHTML = newHtml;
    }, [rqMarkdown]);

    // User types in the RQ contentEditable → convert HTML → markdown → assemble
    const handleRqInput = useCallback((e) => {
        isRqInputRef.current = true;
        setRqMarkdown(rqHtmlToMd(e.currentTarget.innerHTML));
    }, []);

    // Paste as plain text to avoid injecting external HTML/styles
    const handleRqPaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        if (text) document.execCommand("insertText", false, text);
    }, []);

    // ── Assembly effect ──────────────────────────────────────────────────────
    // Whenever any local section changes (user edit), rebuild the full protocol
    // markdown and push it to the parent.  isFirstAssemblyRef skips the very
    // first run (initial state setup) to avoid an unnecessary parent update.
    const isFirstAssemblyRef = useRef(true);
    useEffect(() => {
        if (isFirstAssemblyRef.current) {
            isFirstAssemblyRef.current = false;
            return;
        }
        if (!mountedRef.current) return;
        isInternalUpdateRef.current = true;
        setProtocolMarkdown(assembleMarkdown(rqMarkdown, picoRows, hierarchyRows, afterMd));
    }, [rqMarkdown, picoRows, hierarchyRows, afterMd]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── External change effect ───────────────────────────────────────────────
    // When the parent's protocolMarkdown changes due to AI generation or import,
    // re-parse all four sections. rqMarkdown sync effect will push the new HTML
    // into the contentEditable DOM automatically.
    useEffect(() => {
        if (!mountedRef.current) { console.log('[Protocol] external effect: mountedRef false, skipping'); return; }
        if (isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false;
            console.log('[Protocol] external effect: internal update, skipping');
            return;
        }
        if (!protocolMarkdown?.trim()) { console.log('[Protocol] external effect: empty protocolMarkdown'); return; }
        console.log('[Protocol] external effect: PARSING, length:', protocolMarkdown.length);
        const parsed = parseMarkdownSections(protocolMarkdown);
        console.log('[Protocol] parsed rqMd:', parsed.rqMd?.slice(0,60), '| picoRows:', parsed.picoRows?.length, '| hierRows:', parsed.hierarchyRows?.length);
        setRqMarkdown(parsed.rqMd);
        setPicoRows(parsed.picoRows);
        setHierarchyRows(parsed.hierarchyRows);
        setAfterMd(parsed.afterMd);
    }, [protocolMarkdown]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Selection highlight helpers ──────────────────────────────────────────
    const applySelectionHighlight = useCallback((range) => {
        removeSelectionHighlight();
        if (!CSS?.highlights) return;
        try {
            const cloned    = range.cloneRange();
            const highlight = new window.Highlight(cloned);
            CSS.highlights.set(HIGHLIGHT_NAME, highlight);
        } catch (e) { /* Ignore */ }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const removeSelectionHighlight = useCallback(() => {
        if (!CSS?.highlights) return;
        try { CSS.highlights.delete(HIGHLIGHT_NAME); } catch (e) { /* ignore */ }
    }, []);

    const handleMouseUp = useCallback(
        (e) => {
            if (!aiMode) return;
            if (selectionPopupRef.current?.contains(e.target)) return;
            setTimeout(() => {
                const selection  = window.getSelection();
                const selectedText = selection?.toString().trim();
                if (!selectedText || selectedText.length < 3) {
                    if (!selectionPopup.visible) return;
                    removeSelectionHighlight();
                    setSelectionPopup(prev => ({ ...prev, visible: false }));
                    return;
                }
                if (!contentAreaRef.current) return;
                if (!contentAreaRef.current.contains(selection.anchorNode)) return;

                const range      = selection.getRangeAt(0);
                const rect       = range.getBoundingClientRect();
                const popupWidth = 340;
                const popupHeight = 220;
                const x = Math.max(8, Math.min(
                    rect.left + rect.width / 2 - popupWidth / 2,
                    window.innerWidth - popupWidth - 8,
                ));
                const areaRect   = contentAreaRef.current.getBoundingClientRect();
                const spaceBelow = areaRect.bottom - rect.bottom;
                const y = spaceBelow < popupHeight + 10
                    ? rect.top  + window.scrollY - popupHeight - 10
                    : rect.bottom + window.scrollY + 10;

                applySelectionHighlight(range);
                setSelectionPopup({ visible: true, x, y, selectedText });
                setRefinePrompt("");
            }, 10);
        },
        [aiMode, selectionPopup.visible, applySelectionHighlight, removeSelectionHighlight],
    );

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                selectionPopupRef.current &&
                !selectionPopupRef.current.contains(e.target) &&
                !contentAreaRef.current?.contains(e.target)
            ) {
                removeSelectionHighlight();
                setSelectionPopup(prev => ({ ...prev, visible: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [removeSelectionHighlight]);

    // ── AI generation ────────────────────────────────────────────────────────
    const MODE_DIRECTIVE = {
        expand:  "Update the existing protocol. Keep every section, table row and value that is already there, and only add or fill in what is missing. Instruction:",
        refine:  "Improve the existing protocol in place. Keep the same sections and the same table rows — rewrite the wording to be more precise and specific. Do not drop any row. Instruction:",
        replace: "Start over and generate a fresh protocol from the abstracts, discarding what exists. Instruction:",
    };

    const onGenerateClick = () => {
        if (!aiPrompt.trim()) return;
        // Assemble the current markdown to check for existing content
        const currentMd = assembleMarkdown(rqMarkdown, picoRows, hierarchyRows, afterMd);
        const filled = parseProtocolToCriteria(currentMd);
        const hasContent = filled.exclusions.length > 0 || filled.inclusions.length > 0;
        if (hasContent) setShowGenerateConfirm(true);
        else handleGenerateProtocol("replace");
    };

    const handleGenerateProtocol = async (mode = "replace") => {
        if (!aiPrompt.trim()) {
            setPopupErrorMessage("Please enter a prompt for AI generation");
            setIsErrorPopupVisible(true);
            return;
        }
        if (!projectId) {
            setPopupErrorMessage("Please create a project first before generating protocol");
            setIsErrorPopupVisible(true);
            return;
        }
        if (!abstractsCount || abstractsCount === 0) {
            setPopupErrorMessage("No abstracts found. Please upload abstract data in General Settings before generating the protocol.");
            setIsErrorPopupVisible(true);
            return;
        }

        setIsGenerating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Generating Protocol using AI");

        try {
            const response = await axios.post(
                `${API_BASE_URL}/generate-screening-protocol`,
                { fabric_id: projectId, prompt: `${MODE_DIRECTIVE[mode]} ${aiPrompt}` },
                { headers },
            );
            const data = response.data;
            if (data.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (data.status === "failed") {
                setPopupErrorMessage(data.message || "Failed to generate protocol");
                setIsErrorPopupVisible(true);
                return;
            }
            if (data.content) {
                console.log('[Protocol] data.content received, length:', data.content.length, 'preview:', data.content.slice(0, 80));
                // Triggers the external change effect → re-parses all sections
                setProtocolMarkdown(data.content);
            } else {
                console.warn('[Protocol] data.content is falsy. data keys:', Object.keys(data));
            }
            setAiPrompt("");
        } catch (err) {
            console.error("Error generating protocol:", err);
            setPopupErrorMessage(err?.response?.data?.message || err.message || "Failed to generate protocol");
            setIsErrorPopupVisible(true);
        } finally {
            setIsGenerating(false);
            setIsProcessingPopupVisible(false);
        }
    };

    const handleRefineSelection = async () => {
        if (!refinePrompt.trim()) { refineInputRef.current?.focus(); return; }
        if (!projectId) { setPopupErrorMessage("Please create a project first"); setIsErrorPopupVisible(true); return; }

        removeSelectionHighlight();
        setSelectionPopup(prev => ({ ...prev, visible: false }));
        setIsGenerating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Refining selected section using AI");

        try {
            const response = await axios.post(
                `${API_BASE_URL}/generate-screening-protocol`,
                { fabric_id: projectId, prompt: refinePrompt, text_to_edit: selectionPopup.selectedText },
                { headers },
            );
            const data = response.data;
            if (data.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (data.status === "failed") { setPopupErrorMessage(data.message || "Failed to refine selection"); setIsErrorPopupVisible(true); return; }
            // Triggers the external change effect → re-parses all sections
            if (data.content) setProtocolMarkdown(data.content);
            setRefinePrompt("");
        } catch (err) {
            setPopupErrorMessage(err?.response?.data?.message || err.message || "Failed to refine selection");
            setIsErrorPopupVisible(true);
        } finally {
            setIsGenerating(false);
            setIsProcessingPopupVisible(false);
        }
    };

    const handlePopupMouseDown = useCallback((e) => {
        if (e.target === refineInputRef.current) return;
        e.preventDefault();
    }, []);

    // ── Tables: formatting toolbar for the currently focused cell ────────────
    // ── Tables toolbar: formatting state ────────────────────────────────────────
    // Updated on every selection change so toolbar buttons reflect the cursor's
    // current formatting (bold/italic/underline/list type).
    const [pteFmt, setPteFmt] = useState({ bold: false, italic: false, underline: false });
    const [pteActiveList, setPteActiveList] = useState(null); // 'ul' | 'ol' | null

    const safeQueryCmd = (cmd) => { try { return document.queryCommandState(cmd); } catch { return false; } };

    useEffect(() => {
        const onSelectionChange = () => {
            const el = document.activeElement;
            const isEditable = el?.classList?.contains?.("pte-cell") ||
                               el?.classList?.contains?.("pte-rq-cell");
            if (!isEditable) {
                setPteFmt({ bold: false, italic: false, underline: false });
                setPteActiveList(null);
                return;
            }
            setPteFmt({
                bold:      safeQueryCmd("bold"),
                italic:    safeQueryCmd("italic"),
                underline: safeQueryCmd("underline"),
            });
            setPteActiveList(
                safeQueryCmd("insertUnorderedList") ? "ul" :
                safeQueryCmd("insertOrderedList")   ? "ol" : null
            );
        };
        document.addEventListener("selectionchange", onSelectionChange);
        return () => document.removeEventListener("selectionchange", onSelectionChange);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Tables toolbar: formatting handlers ──────────────────────────────────
    const pteExecFmt = useCallback((cmd) => {
        const el = document.activeElement;
        const isEditable = el?.classList?.contains?.("pte-cell") ||
                           el?.classList?.contains?.("pte-rq-cell");
        if (!isEditable) return;
        document.execCommand(cmd);
    }, []);

    const handleBold      = useCallback(() => pteExecFmt("bold"),      [pteExecFmt]);
    const handleItalic    = useCallback(() => pteExecFmt("italic"),    [pteExecFmt]);
    const handleUnderline = useCallback(() => pteExecFmt("underline"), [pteExecFmt]);

    const handleInsertBullet = useCallback(() => {
        const el = document.activeElement;
        const isEditable = el?.classList?.contains?.("pte-cell") ||
                           el?.classList?.contains?.("pte-rq-cell");
        if (!isEditable) return;
        document.execCommand("insertUnorderedList");
    }, []);

    const handleInsertOrderedBullet = useCallback(() => {
        const el = document.activeElement;
        const isEditable = el?.classList?.contains?.("pte-cell") ||
                           el?.classList?.contains?.("pte-rq-cell");
        if (!isEditable) return;
        document.execCommand("insertOrderedList");
    }, []);

    // ── Hierarchy reorder (uses hierarchyRows state directly) ────────────────
    const openReorder = () => {
        if (hierarchyRows.length < 2) {
            setPopupErrorMessage("The Hierarchy of Selection table needs at least two rows before it can be reordered.");
            setIsErrorPopupVisible(true);
            return;
        }
        setReorderRows(
            hierarchyRows.map(([criterion, rule, stage, reason], i) => ({
                criterion: criterion || "",
                rule:      rule      || "",
                stage:     stage     || "",
                reason:    reason    || "",
                key: `hr-${i}`,
            })),
        );
    };

    const applyReorder = () => {
        const next = reorderRows.map(({ criterion, rule, stage, reason }) => [
            criterion || "", rule || "", stage || "", reason || "",
        ]);
        setHierarchyRows(next);
        setReorderRows(null);
    };

    // ── Import success callback ──────────────────────────────────────────────
    const handleImportSuccess = useCallback((newMarkdown) => {
        setIsImportSelectorOpen(false);
        const parsed = parseMarkdownSections(newMarkdown);
        setRqMarkdown(parsed.rqMd);
        setPicoRows(parsed.picoRows);
        setHierarchyRows(parsed.hierarchyRows);
        setAfterMd(parsed.afterMd);
        // rqMarkdown sync effect will push the new HTML into the contentEditable DOM
    }, []);

    // ── Protocol template: download ──────────────────────────────────────────
    const handleGenerateReport = async () => {
        if (!configId) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Opening report...");
        try {
            const res = await axios.post(
                `/pceapi/slr/genbi-templates/list`,
                { slr_fabric_id: configId },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            const templates = res.data?.status === "success" ? (res.data.data || []) : [];

            if (templates.length === 0) {
                setPopupErrorMessage("No report templates available for this project.");
                setIsErrorPopupVisible(true);
                setIsProcessingPopupVisible(false);
                setProcessingMessage("");
                return;
            }

            // Find the SLR Protocol Report template; fall back to first if not found
            const target = templates.find(
                (t) => t.template_name === "SLR Protocol Report"
            ) || templates[0];

            await handleOpenTemplate(target);
        } catch (err) {
            console.error("Failed to fetch GenBI templates", err);
            setPopupErrorMessage("Failed to load report templates.");
            setIsErrorPopupVisible(true);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleOpenTemplate = async (template) => {
        setShowReportDropdown(false);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Opening Gen BI workspace...");
        try {
            const res = await axios.post(
                `/pceapi/slr/genbi-templates/create-or-open`,
                { slr_genbi_template_id: template.id, slr_fabric_id: configId },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (res.data?.status !== "success") {
                setPopupErrorMessage(res.data?.message || "Failed to open report template");
                setIsErrorPopupVisible(true);
                return;
            }
            const action       = res.data.action;
            const templateId   = res.data.data?.id || res.data.template_id;
            const genBiFabricId = res.data.data?.fabric_id || res.data.fabric_id;
            if (!templateId || !genBiFabricId) {
                setPopupErrorMessage("Missing data in response");
                setIsErrorPopupVisible(true);
                return;
            }
            onOpenGenbi?.({
                fromSLR: true,
                slrFabricId: genBiFabricId,
                slrTemplateName: template.template_name,
                templateId,
                action,
                templateStructure: action === "created" ? res.data.data : null,
                fabricIdForTextractor: projectId,
            });
        } catch (err) {
            console.error("Failed to open template", err);
            setPopupErrorMessage("Failed to open report template");
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/protocol-template/download`, {
                headers,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(res.data);
            const a   = document.createElement("a");
            a.href     = url;
            a.download = "Protocol_Template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const data = err?.response?.data;
            if (data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            setPopupErrorMessage(data?.message || err.message || "Failed to download protocol template");
            setIsErrorPopupVisible(true);
        }
    };

    const handleTemplateFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (templateFileRef.current) templateFileRef.current.value = "";

        setTemplateNotice("");
        setIsUploadingTemplate(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Reading uploaded protocol template");

        try {
            const fd = new FormData();
            fd.append("protocol_file", file);
            const response = await axios.post(
                `${API_BASE_URL}/upload-protocol-template`,
                fd,
                { headers: { ...headers, "Content-Type": "multipart/form-data" } },
            );
            const data = response.data;
            if (data.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
            if (data.status === "failed") {
                setPopupErrorMessage(data.message || "Failed to read protocol template");
                setIsErrorPopupVisible(true);
                return;
            }
            if (data.markdown) {
                handleImportSuccess(data.markdown);
                setTemplateNotice(data.message || "");
            }
        } catch (err) {
            setPopupErrorMessage(err?.response?.data?.message || err.message || "Failed to upload protocol template");
            setIsErrorPopupVisible(true);
        } finally {
            setIsUploadingTemplate(false);
            setIsProcessingPopupVisible(false);
        }
    };

    // ────────────────────────────────────────────────────────────────────────
    return (
        <div className="w-100 h-100 d-flex flex-column overflow-hidden">

            {/* ── Protocol toolbar ──────────────────────────────────────── */}
            <div className="slr-protocol-toolbar">
                <div className="d-flex align-items-center gap-3 w-100 mb-2">

                    {/* AI mode switch */}
                    <div className="slr-ai-mode-group">
                        <span className="ev-reg-p-dark ev-bold-med">AI mode</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={aiMode}
                            aria-label="Toggle AI mode"
                            className={`slr-ai-switch${aiMode ? " active" : ""}`}
                            onClick={() => setAiMode((prev) => !prev)}
                        >
                            <span className="slr-ai-switch-knob" />
                        </button>
                        {!aiMode && <span className="ev-small-p-dark opacity-75">OFF</span>}
                        <i
                            className="bi bi-info-circle ev-small-p-blue slr-ai-mode-info"
                            title="AI mode lets you generate and refine the protocol with AI. Turn it off to fill the protocol from an Excel template instead."
                        />
                    </div>

                    {/* Right-side actions */}
                    <div className="slr-protocol-toolbar-actions">
                        {!aiMode && (
                            <>
                                <button
                                    type="button"
                                    className="ev-btn-blue-outline-round ev-btn-reg ev-bold-reg"
                                    onClick={handleDownloadTemplate}
                                >
                                    <i className="bi bi-download me-1" />
                                    Download Template
                                </button>

                                <button
                                    type="button"
                                    className="ev-btn-dark-blue-fill-round ev-btn-reg ev-bold-reg"
                                    onClick={() => templateFileRef.current?.click()}
                                    disabled={isUploadingTemplate}
                                >
                                    <i className="bi bi-upload me-1" />
                                    Upload Protocol Template
                                </button>

                                <input
                                    type="file"
                                    ref={templateFileRef}
                                    className="d-none"
                                    accept=".xlsx"
                                    onChange={handleTemplateFileChange}
                                />
                            </>
                        )}

                        <button
                            className="ev-btn-blue-outline-round ev-btn-reg ev-bold-reg flex-shrink-0"
                            onClick={openReorder}
                            disabled={isGenerating}
                        >
                            <i className="bi bi-arrow-down-up me-1" />
                            Reorder Hierarchy
                        </button>

                        {/* Generate Report button */}
                        {/* <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-reg ev-bold-reg flex-shrink-0"
                            onClick={handleGenerateReport}
                            disabled={!configId}
                        >
                            <i className="bi bi-file-earmark-bar-graph-fill me-1" />
                            Generate Report
                        </button> */}

                        {/* Import Protocol button + selector */}
                        <div className="position-relative flex-shrink-0">
                            <button
                                ref={importBtnRef}
                                type="button"
                                className={`ev-btn-blue-fill-round ev-btn-reg ev-bold-reg avatar-tooltip${isImportSelectorOpen ? " active" : ""}`}
                                onClick={() => setIsImportSelectorOpen((prev) => !prev)}
                            >
                                <i className="bi bi-file-earmark-arrow-down-fill me-1" />
                                Import Protocol
                            </button>

                            {isImportSelectorOpen && (
                                <div
                                    ref={importSelectorRef}
                                    className="position-absolute z-1000 end-0 mt-1 slr-import-panel"
                                >
                                    <ProtocolDocSelector
                                        projectId={projectId}
                                        onClose={() => setIsImportSelectorOpen(false)}
                                        onImportSuccess={handleImportSuccess}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* AI prompt row */}
                {aiMode && (
                    <div className="d-flex align-items-center gap-3 w-100 mb-1">
                        <span className="ev-reg-p-dark-blue ev-bold-med text-nowrap">
                            Generate Protocol using AI
                            <i className="bi bi-stars gradient-font9 ms-1" />
                        </span>

                        <div className="position-relative flex-grow-1">
                            <input
                                type="text"
                                className="formField4 w-100 slr-input-with-btn"
                                placeholder="Add your prompt here"
                                value={aiPrompt}
                                onChange={(e) => setAiPrompt(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !isGenerating && onGenerateClick()}
                            />
                            <button
                                type="button"
                                className="ev-btn-gradient-fill-round ev-btn-reg slr-input-inner-btn ev-radius-xsmall"
                                onClick={onGenerateClick}
                                disabled={isGenerating}
                            >
                                Generate
                            </button>
                        </div>
                    </div>
                )}

                {/* Template upload result notice */}
                {templateNotice && (
                    <div className="slr-protocol-notice">
                        <i className="bi bi-check-circle me-2" />
                        <span className="ev-small-p-dark">{templateNotice}</span>
                        <button
                            type="button"
                            className="slr-protocol-notice-close"
                            aria-label="Dismiss"
                            onClick={() => setTemplateNotice("")}
                        >
                            <i className="bi bi-x" />
                        </button>
                    </div>
                )}
            </div>

            {/* ── Scrollable content: RQ editor + tables ────────────────── */}
            <div
                ref={contentAreaRef}
                className="flex-grow-1 min-h-0 scrollable-element d-flex flex-column gap-2 pb-3 pe-1"
                onMouseUp={handleMouseUp}
            >

                {/* Unified formatting toolbar — works for RQ editor and all table cells */}
                <div className="pte-tables-toolbar">
                    <button
                        type="button"
                        className={`pte-toolbar-btn${pteActiveList === "ul" ? " active" : ""}`}
                        title="Bullet list"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleInsertBullet}
                    >
                        <i className="bi bi-list-ul" />
                    </button>
                    <button
                        type="button"
                        className={`pte-toolbar-btn${pteActiveList === "ol" ? " active" : ""}`}
                        title="Numbered list"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleInsertOrderedBullet}
                    >
                        <i className="bi bi-list-ol" />
                    </button>
                    <span className="pte-toolbar-separator" />
                    <button
                        type="button"
                        className="pte-toolbar-btn"
                        title="Bold"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleBold}
                    >
                        <i className={`bi bi-type-bold${pteFmt.bold ? " pte-fmt-active" : ""}`} />
                    </button>
                    <button
                        type="button"
                        className="pte-toolbar-btn"
                        title="Italic"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleItalic}
                    >
                        <i className={`bi bi-type-italic${pteFmt.italic ? " pte-fmt-active" : ""}`} />
                    </button>
                    <button
                        type="button"
                        className="pte-toolbar-btn"
                        title="Underline"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleUnderline}
                    >
                        <i className={`bi bi-type-underline${pteFmt.underline ? " pte-fmt-active" : ""}`} />
                    </button>
                </div>

                {/* Research Questions — contentEditable (same pattern as table cells) */}
                <div
                    ref={editorWrapperRef}
                    className="ev-card-8 position-relative"
                >
                    <div
                        ref={rqCellRef}
                        className="scrollable-element vh-20 p-1 pte-cell"
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder="Start writing your research questions..."
                        onInput={handleRqInput}
                        onPaste={handleRqPaste}
                    />
                </div>

                {/* PICO Criteria Table */}
                <div className="ev-card-8 p-3">
                    <p className="ev-bold-med ev-reg-p-dark-blue mb-2 mt-0">PICO Criteria Table</p>
                    <ProtocolTableEditor
                        headers={["Criteria", "Include", "Exclude"]}
                        rows={picoRows}
                        onChange={setPicoRows}
                        colWidths={["18%", "41%", "41%"]}
                        canAddRows
                        canDeleteRows
                        minRows={1}
                        defaultNewRow={["", "", ""]}
                    />
                </div>

                {/* Hierarchy of Selection */}
                <div className="ev-card-8 p-3">
                    <p className="ev-bold-med ev-reg-p-dark-blue mb-2 mt-0">Hierarchy of Selection</p>
                    <ProtocolTableEditor
                        headers={["Criterion", "Rule", "Stage", "Exclusion Reason if Not Met"]}
                        rows={hierarchyRows}
                        onChange={setHierarchyRows}
                        colWidths={["36%", "14%", "12%", "38%"]}
                        showNumberColumn
                        canAddRows
                        canDeleteRows
                        minRows={1}
                        defaultNewRow={["", "Must have", "Both", ""]}
                    />
                </div>

            </div>

            {/* ── Selection Refine Popup ────────────────────────────────── */}
            {aiMode && selectionPopup.visible && (
                <div
                    ref={selectionPopupRef}
                    className="slr-selection-popup"
                    style={{ top: selectionPopup.y, left: selectionPopup.x }}
                    onMouseDown={handlePopupMouseDown}
                >
                    <div className="slr-selection-popup-header">
                        <i className="bi bi-stars gradient-font9 me-1" />
                        <span className="ev-bold-med ev-reg-p-dark-blue">Refine selected text using AI</span>
                        <button
                            className="slr-selection-popup-close"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => {
                                removeSelectionHighlight();
                                setSelectionPopup(prev => ({ ...prev, visible: false }));
                            }}
                        >
                            <i className="bi bi-x" />
                        </button>
                    </div>
                    <div className="slr-selection-popup-preview">
                        <i className="bi bi-quote me-1 opacity-50" />
                        <span className="slr-selection-popup-preview-text">
                            {selectionPopup.selectedText.length > 90
                                ? selectionPopup.selectedText.slice(0, 90) + "…"
                                : selectionPopup.selectedText}
                        </span>
                    </div>
                    <textarea
                        ref={refineInputRef}
                        className="slr-selection-popup-input"
                        placeholder="e.g. 'Make stricter', 'Add RCT requirement', 'Simplify language'..."
                        value={refinePrompt}
                        rows={3}
                        onChange={(e) => setRefinePrompt(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleRefineSelection();
                        }}
                    />
                    <div className="slr-selection-popup-footer">
                        <span className="ev-small-p opacity-50">Ctrl+Enter to submit</span>
                        <button
                            type="button"
                            className="ev-btn-gradient-fill-round ev-btn-sm ev-radius-xsmall"
                            onClick={handleRefineSelection}
                            disabled={isGenerating || !refinePrompt.trim()}
                        >
                            <i className="bi bi-pencil me-1" />Refine
                        </button>
                    </div>
                </div>
            )}

            {/* ── Generate mode picker popup ────────────────────────────── */}
            {showGenerateConfirm && (() => {
                // Every user edit immediately updates protocolMarkdown via the assembly effect,
                // so there is no "unsaved edits" concept in this architecture.
                const isDirty = false;
                return (
                    <div className="popup" onClick={() => setShowGenerateConfirm(false)}>
                        <div
                            className="popup-content slr-genmode-picker"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <p className="ev-med-p-blue ev-bold-med mb-1">You already have a protocol</p>
                            <p className="ev-small-p-dark mb-3">Choose what AI should do with it.</p>

                            {isDirty && (
                                <p className="ev-alert-card-warning ev-small-p-dark p-2 mb-3 ev-radius-small">
                                    You have unsaved edits. AI works from the last saved version,
                                    so anything you have typed since then will be lost. Save first to keep it.
                                </p>
                            )}

                            {[
                                {
                                    key: "expand",
                                    title: "Keep and expand",
                                    desc: "Your protocol stays as it is. AI fills in what is missing based on your prompt.",
                                },
                                {
                                    key: "refine",
                                    title: "Keep and improve",
                                    desc: "AI rewrites the wording to be more precise. Sections and table rows stay the same.",
                                },
                                {
                                    key: "replace",
                                    title: "Replace everything",
                                    desc: "The current protocol is discarded and regenerated from the abstracts.",
                                },
                            ].map((m) => (
                                <button
                                    key={m.key}
                                    className="slr-genmode-option"
                                    onClick={() => {
                                        setShowGenerateConfirm(false);
                                        handleGenerateProtocol(m.key);
                                    }}
                                >
                                    <span className="ev-reg-p-dark ev-bold-reg d-block">{m.title}</span>
                                    <span className="ev-small-p-dark">{m.desc}</span>
                                </button>
                            ))}

                            <div className="d-flex justify-content-end mt-2">
                                <button
                                    className="ev-btn-light-fill ev-btn-small"
                                    onClick={() => setShowGenerateConfirm(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Hierarchy reorder popup ───────────────────────────────── */}
            {reorderRows && (
                <div className="popup" onClick={() => setReorderRows(null)}>
                    <div className="popup-content slr-reorder-picker" onClick={(e) => e.stopPropagation()}>
                        <p className="ev-med-p-blue ev-bold-med mb-1">Reorder screening hierarchy</p>
                        <p className="ev-small-p-dark mb-3">
                            A record is excluded by the first criterion it fails, so this order
                            decides which reason it gets. Numbering updates automatically.
                        </p>

                        <DragDropContext
                            onDragEnd={(res) => {
                                if (!res.destination) return;
                                setReorderRows((prev) => {
                                    const next = [...prev];
                                    const [moved] = next.splice(res.source.index, 1);
                                    next.splice(res.destination.index, 0, moved);
                                    return next;
                                });
                            }}
                        >
                            <Droppable droppableId="hierarchy">
                                {(dp) => (
                                    <div ref={dp.innerRef} {...dp.droppableProps} className="slr-reorder-list">
                                        {reorderRows.map((r, i) => (
                                            <Draggable key={r.key} draggableId={r.key} index={i}>
                                                {(dr) => (
                                                    <div
                                                        ref={dr.innerRef}
                                                        {...dr.draggableProps}
                                                        {...dr.dragHandleProps}
                                                        className="slr-reorder-row d-flex align-items-center"
                                                    >
                                                        <span className="slr-reorder-num ev-bold-reg">{i + 1}</span>
                                                        <i className="bi bi-grip-vertical me-2 ev-small-p-dark" />
                                                        <span className="flex-grow-1 ev-small-p-dark text-truncate">
                                                            {htmlToPlainText(r.criterion) || "(empty)"}
                                                        </span>
                                                        <span className="ev-xsmall-p-blue ms-2 text-nowrap">{htmlToPlainText(r.reason)}</span>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {dp.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button
                                className="ev-btn-light-fill ev-btn-small"
                                onClick={() => setReorderRows(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="ev-btn-blue-fill-round ev-btn-small"
                                onClick={applyReorder}
                            >
                                Apply order
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── GenBI template picker ────────────────────────────────── */}
            {showReportDropdown && (
                <div className="popup" onClick={() => setShowReportDropdown(false)}>
                    <div
                        className="popup-content slr-genbi-picker"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <p className="ev-med-p-blue ev-bold-med mb-0">
                                Reports & Synthesis Templates
                            </p>
                            <button
                                className="ev-btn-light-fill ev-btn-small"
                                onClick={() => setShowReportDropdown(false)}
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {loadingTemplates ? (
                            <p className="ev-small-p-dark text-center py-2">Loading templates...</p>
                        ) : genbiTemplates.length === 0 ? (
                            <p className="ev-small-p-dark text-center py-2">No templates available</p>
                        ) : (
                            <div className="slr-genbi-picker-grid">
                                {genbiTemplates.map((template) => (
                                    <div
                                        key={template.id}
                                        className="ev-card-2 slr-genbi-picker-row ev-radius-small"
                                    >
                                        <span className="ev-small-p-dark ev-bold-med">
                                            {template.template_name}
                                        </span>
                                        <button
                                            type="button"
                                            className="ev-btn-blue-outline-round ev-btn-small ms-2"
                                            disabled={!template.is_active}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenTemplate(template);
                                            }}
                                        >
                                            Open
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
            {isErrorPopupVisible && (
                <ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />
            )}

        </div>
    );
};

export default ScreeningProtocolForm;